export const RPT_SUBTOPIC_ALL = '__ALL__';

export interface RptSubTopicOption {
    value: string;
    name: string;
}

export const getAllSubTopicCodes = (options: RptSubTopicOption[] | undefined): string[] =>
    (options ?? []).map((item) => item.value);

export const isAllSubTopicsSelected = (selected: string[], options: RptSubTopicOption[] | undefined): boolean => {
    const allCodes = getAllSubTopicCodes(options);
    if (allCodes.length === 0) {
        return false;
    }
    return allCodes.every((code) => selected.includes(code)) && selected.length === allCodes.length;
};

export const handleSubTopicSelectionChange = (
    rawValue: unknown,
    currentSelected: string[],
    options: RptSubTopicOption[] | undefined
): string[] => {
    const value = rawValue as string[];
    const allCodes = getAllSubTopicCodes(options);

    if (value.includes(RPT_SUBTOPIC_ALL)) {
        return isAllSubTopicsSelected(currentSelected, options) ? [] : allCodes;
    }

    return value.filter((code) => code !== RPT_SUBTOPIC_ALL);
};

export const formatSelectedSubTopicLabels = (
    selected: string[],
    options: RptSubTopicOption[] | undefined
): string =>
    (options ?? [])
        .filter((item) => selected.includes(item.value))
        .map((item) => item.name)
        .join(', ');

export const formatSelectedSubTopicDisplay = (
    selected: string[],
    options: RptSubTopicOption[] | undefined,
    allLabel: string
): string => {
    if (selected.length === 0) {
        return '';
    }
    if (isAllSubTopicsSelected(selected, options)) {
        return allLabel;
    }
    return formatSelectedSubTopicLabels(selected, options);
};

export interface RptOrderApiItem {
    customerNo?: string | number;
    orderNo?: string;
    rptOrderNo?: string;
    /** get-rpt-orders response — zaman kuralı gruplaması bu alana göre yapılır */
    subTopicCode?: string;
    subTopicTR?: string;
    createdOn?: string;
}

export interface RptOrderGridRow {
    customerNo: string | number;
    orderNo: string;
    rptOrderNo: string;
    subTopicTR: string;
    subTopicCode: string;
    createdOn: string;
    ruleViolationHighlight: boolean;
}

export const RPT_HISTORY_ROW_VIOLATION_CLASS = 'rpt-history-datagrid-row--rule-violation';

const getRptOrderGroupKey = (item: RptOrderApiItem | Pick<RptOrderGridRow, 'subTopicCode'>): string =>
    (item.subTopicCode ?? '').trim() || '__ungrouped__';

export const getRptSubTopicCounts = (orders: RptOrderApiItem[]): Map<string, number> => {
    const countsBySubTopic = new Map<string, number>();

    for (const item of orders) {
        const key = getRptOrderGroupKey(item);
        countsBySubTopic.set(key, (countsBySubTopic.get(key) ?? 0) + 1);
    }

    return countsBySubTopic;
};

export const getRptViolatingSubTopicCodes = (orders: RptOrderApiItem[]): Set<string> => {
    const violating = new Set<string>();

    for (const [code, count] of getRptSubTopicCounts(orders)) {
        if (count >= 2) {
            violating.add(code);
        }
    }

    return violating;
};

/** Aynı temas nedeninde 2+ RPT kaydı varsa zaman kuralı ihlal edilmiş sayılır (çoklu seçimde toplam kayıt sayısına bakılmaz). */
export const isRptTimeRuleViolated = (orders: RptOrderApiItem[]): boolean =>
    getRptViolatingSubTopicCodes(orders).size > 0;

export const getRptHistoryRowClassName = (row: RptOrderGridRow): string =>
    row.ruleViolationHighlight ? RPT_HISTORY_ROW_VIOLATION_CLASS : '';

export const mapRptOrdersToGridRows = (
    orders: RptOrderApiItem[],
    formatCreatedOn: (value: string | undefined) => string
): RptOrderGridRow[] => {
    const violatingCodes = getRptViolatingSubTopicCodes(orders);

    return orders.map((item) => {
        const subTopicCode = (item.subTopicCode ?? '').trim();
        const groupKey = subTopicCode || '__ungrouped__';

        return {
            customerNo: item.customerNo || '-',
            orderNo: item.orderNo || ' ',
            rptOrderNo: item.rptOrderNo || ' ',
            subTopicTR: item.subTopicTR || '-',
            subTopicCode,
            createdOn: formatCreatedOn(item.createdOn) || ' - ',
            ruleViolationHighlight: violatingCodes.has(groupKey),
        };
    });
};
